import React, { ChangeEventHandler, useMemo } from 'react';
import { Col, Radio } from 'antd';
import { translate } from '@portal/services/i18n';
import Modal from 'antd/lib/modal/Modal';
import AdvancedButton from '@portal/components/AdvancedButton/AdvancedButton';
import { DocumentType } from '@portal/enum/documentType';
import { onboarding } from '@portal/config/app.json';
import { CloseOutlined } from '@ant-design/icons';

interface IProps {
  documentType: number;
  onFileChangeCnh: ChangeEventHandler<HTMLInputElement>;
  onFileChangeDigitalCnh: ChangeEventHandler<HTMLInputElement>;
  onFileChangeCnhBack?: ChangeEventHandler<HTMLInputElement>;
  onFileChangeRgBack: ChangeEventHandler<HTMLInputElement>;
  onFileChangeRgFront: ChangeEventHandler<HTMLInputElement>;
  cleanFileCnh?: () => void;
  cleanFileCnhBack?: () => void;
  cleanFileRgFront?: () => void;
  cleanFileRgBack?: () => void;
  modal: boolean;
  isUserMinor?: boolean;
  onCancel: () => void;
  setDocumentType(value: number);
  onFileChangeCnhModal: File | null;
  onFileChangeDigitalCnhModal: File | null;
  onFileChangeCnhModalBack?: File | null;
  onFileChangeRgFrontModal: File | null;
  onFileChangeRgBackModal: File | null;
  sendType?: string;
}
const OnboardingDocumentPhotoModal = ({
  documentType,
  setDocumentType,
  onFileChangeCnh,
  onFileChangeDigitalCnh,
  onFileChangeCnhBack,
  onFileChangeRgBack,
  onFileChangeRgFront,
  cleanFileCnh,
  cleanFileCnhBack,
  cleanFileRgBack,
  cleanFileRgFront,
  modal,
  isUserMinor,
  onCancel,
  onFileChangeCnhModal,
  onFileChangeDigitalCnhModal,
  onFileChangeCnhModalBack,
  onFileChangeRgFrontModal,
  onFileChangeRgBackModal,
  sendType,
}: IProps) => {
  const handleCloseModal = () => {
    onCancel();
  };

  const cnhAlreadyUploaded: boolean = useMemo(() => {
    if (documentType === DocumentType.CNH) {
      if (onFileChangeCnhModal?.name || onFileChangeCnhModalBack?.name) {
        return true;
      }
    }

    return false;
  }, [
    documentType,
    onFileChangeCnhModal?.name,
    onFileChangeCnhModalBack?.name,
  ]);

  const digitalCnhAlreadyUploaded: boolean = useMemo(() => {
    if (documentType === DocumentType.DIGITAL_CNH) {
      if (onFileChangeDigitalCnhModal?.name) {
        return true;
      }
    }

    return false;
  }, [documentType, onFileChangeDigitalCnhModal?.name]);

  const rgAlreadyUploaded: boolean = useMemo(() => {
    if (documentType === DocumentType.RG) {
      if (onFileChangeRgBackModal?.name || onFileChangeRgFrontModal?.name) {
        return true;
      }
    }

    return false;
  }, [
    documentType,
    onFileChangeRgBackModal?.name,
    onFileChangeRgFrontModal?.name,
  ]);

  return (
    <Modal
      open={modal}
      footer={null}
      closable={true}
      onCancel={() => handleCloseModal()}
      className="message-modal"
    >
      <p className="message-modal__title">
        {translate('PAGES.AUTH.REGISTER.ONBOARD.PF.USER_PHOTO.FORM.BUTTON')}
      </p>

      {onboarding.acceptPdfDocs && sendType === 'photo' ? (
        <div className="message-modal__instructions">
          <p>
            {translate(
              'PAGES.AUTH.REGISTER.ONBOARD.PF.DOCUMENT.FORM.INSTRUCTIONS.REQUIRED'
            )}
          </p>
          <p>
            {translate(
              'PAGES.AUTH.REGISTER.ONBOARD.PF.DOCUMENT.FORM.INSTRUCTIONS.FORMAT'
            )}
          </p>
          <p>
            {translate(
              'PAGES.AUTH.REGISTER.ONBOARD.PF.DOCUMENT.FORM.INSTRUCTIONS.READABLE'
            )}
          </p>
          <p>
            {translate(
              'PAGES.AUTH.REGISTER.ONBOARD.PF.DOCUMENT.FORM.INSTRUCTIONS.ERASURES'
            )}
          </p>
        </div>
      ) : null}
      <Col>
        {!isUserMinor && (
          <Col span={24} className="onboarding-document-photo">
            <Radio.Group
              name="documentType"
              defaultValue={isUserMinor ? DocumentType.RG : DocumentType.CNH}
              value={documentType}
            >
              <Radio
                value={DocumentType.RG}
                onClick={() => setDocumentType(DocumentType.RG)}
                className="new-contact-modal__form__radio-label"
                disabled={cnhAlreadyUploaded || digitalCnhAlreadyUploaded}
              >
                {translate(
                  'PAGES.AUTH.REGISTER.ONBOARD.PF.DOCUMENT.FORM.DOCUMENT_TYPE.RG'
                )}
              </Radio>

              {onboarding.driversLicense ||
              onboarding.requireTwodriverLicensePictures ? (
                <Radio
                  value={DocumentType.CNH}
                  onClick={() => setDocumentType(DocumentType.CNH)}
                  className="new-contact-modal__form__radio-label"
                  disabled={digitalCnhAlreadyUploaded || rgAlreadyUploaded}
                >
                  {translate(
                    'PAGES.AUTH.REGISTER.ONBOARD.PF.DOCUMENT.FORM.DOCUMENT_TYPE.CNH'
                  )}
                </Radio>
              ) : null}

              {onboarding.digitalDriverLicense ? (
                <Radio
                  value={DocumentType.DIGITAL_CNH}
                  onClick={() => setDocumentType(DocumentType.DIGITAL_CNH)}
                  className="new-contact-modal__form__radio-label"
                  disabled={cnhAlreadyUploaded || rgAlreadyUploaded}
                >
                  {translate(
                    'PAGES.AUTH.REGISTER.ONBOARD.PF.DOCUMENT.FORM.DOCUMENT_TYPE.DIGITAL_CNH'
                  )}
                </Radio>
              ) : null}
            </Radio.Group>
          </Col>
        )}
        <Col className="onboarding-document-photo__wrapper__documents">
          {documentType === DocumentType.CNH && !isUserMinor && (
            <div>
              <div className="onboarding-document-photo__text">
                {onboarding.driversLicense &&
                !onboarding.requireTwodriverLicensePictures ? (
                  <p className="select-rg-label">
                    {translate(
                      'PAGES.AUTH.REGISTER.ONBOARD.PF.DOCUMENT.FORM.LABELS.CNH.DEFAULT'
                    )}
                  </p>
                ) : (
                  <p className="select-rg-label">
                    {translate(
                      'PAGES.AUTH.REGISTER.ONBOARD.PF.DOCUMENT.FORM.LABELS.CNH.FRONT'
                    )}
                  </p>
                )}
              </div>

              <div className={'onboarding-user-photo'}>
                {!onFileChangeCnhModal?.name ? (
                  <div className={'onboarding-user-photo__container'}>
                    <div>
                      <label
                        htmlFor="file-cnh"
                        className="onboarding-user-photo__button"
                      >
                        {translate(
                          'PAGES.AUTH.REGISTER.ONBOARD.PF.DOCUMENT.FORM.LOAD_FILE'
                        )}
                      </label>
                      <div className="onboarding-user-photo__title">
                        {onboarding.maxFileSizeImage.enabled ? (
                          <p>
                            {translate(
                              'PAGES.AUTH.REGISTER.ONBOARD.PF.DOCUMENT.FORM.UPLOAD_MAX_IMAGE'
                            )}
                            {onboarding.maxFileSizeImage.sizeInMb}
                            {translate('SHARED.MB')}
                          </p>
                        ) : null}
                        {onboarding.maxFileSizePdf.enabled ? (
                          <p>
                            {translate(
                              'PAGES.AUTH.REGISTER.ONBOARD.PF.DOCUMENT.FORM.UPLOAD_MAX_PDF'
                            )}
                            {onboarding.maxFileSizePdf.sizeInMb}
                            {translate('SHARED.MB')}
                          </p>
                        ) : null}
                      </div>
                      <input
                        className="onboarding-user-photo__input"
                        type="file"
                        name="file-cnh"
                        id="file-cnh"
                        onChange={onFileChangeCnh}
                        accept={`image/png, image/jpeg, ${
                          onboarding.acceptPdfDocs ? 'application/pdf' : ''
                        }`}
                      />
                    </div>
                  </div>
                ) : (
                  <div>
                    <div className="message-modal__fileSelected-button">
                      <span>
                        <div onClick={cleanFileCnh}>
                          <CloseOutlined />
                        </div>
                        {onboarding.acceptPdfDocs &&
                        onFileChangeCnhModal.type === 'application/pdf' ? (
                          <iframe
                            src={(() =>
                              URL.createObjectURL(onFileChangeCnhModal))()}
                            className="pending-document-modal__preview__container__image"
                            title="upload document"
                          />
                        ) : (
                          <img
                            src={URL.createObjectURL(onFileChangeCnhModal)}
                            alt="upload image"
                            className="pending-document-modal__preview__container__image"
                          />
                        )}
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {onboarding.requireTwodriverLicensePictures ? (
                <>
                  <div className="onboarding-document-photo__text">
                    <p className="select-rg-label">
                      {translate(
                        'PAGES.AUTH.REGISTER.ONBOARD.PF.DOCUMENT.FORM.LABELS.CNH.BACK'
                      )}
                    </p>
                  </div>
                  <div className="onboarding-user-photo">
                    {!onFileChangeCnhModalBack?.name ? (
                      <div className={'onboarding-user-photo__container'}>
                        <div>
                          <label
                            htmlFor="file-cnh-back"
                            className="onboarding-user-photo__button"
                          >
                            {translate(
                              'PAGES.AUTH.REGISTER.ONBOARD.PF.DOCUMENT.FORM.LOAD_FILE'
                            )}
                          </label>
                          <div className="onboarding-user-photo__title">
                            {onboarding.maxFileSizeImage.enabled ? (
                              <p>
                                {translate(
                                  'PAGES.AUTH.REGISTER.ONBOARD.PF.DOCUMENT.FORM.UPLOAD_MAX_IMAGE'
                                )}
                                {onboarding.maxFileSizeImage.sizeInMb}
                                {translate('SHARED.MB')}
                              </p>
                            ) : null}
                            {onboarding.maxFileSizePdf.enabled ? (
                              <p>
                                {translate(
                                  'PAGES.AUTH.REGISTER.ONBOARD.PF.DOCUMENT.FORM.UPLOAD_MAX_PDF'
                                )}
                                {onboarding.maxFileSizePdf.sizeInMb}
                                {translate('SHARED.MB')}
                              </p>
                            ) : null}
                          </div>
                          <input
                            className="onboarding-user-photo__input"
                            type="file"
                            name="file-cnh-back"
                            id="file-cnh-back"
                            onChange={onFileChangeCnhBack}
                            accept={`image/png, image/jpeg, ${
                              onboarding.acceptPdfDocs
                                ? 'application/pdf, .pdf'
                                : ''
                            }`}
                          />
                        </div>
                      </div>
                    ) : (
                      <div>
                        <div className="message-modal__fileSelected-button">
                          <span>
                            <div onClick={cleanFileCnhBack}>
                              <CloseOutlined />
                            </div>
                            {onboarding.acceptPdfDocs &&
                            onFileChangeCnhModalBack.type ===
                              'application/pdf' ? (
                              <iframe
                                src={(() =>
                                  URL.createObjectURL(
                                    onFileChangeCnhModalBack
                                  ))()}
                                className="pending-document-modal__preview__container__image"
                                title="upload document"
                              />
                            ) : (
                              <img
                                src={URL.createObjectURL(
                                  onFileChangeCnhModalBack
                                )}
                                alt="upload image"
                                className="pending-document-modal__preview__container__image"
                              />
                            )}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </>
              ) : null}
            </div>
          )}

          {documentType === DocumentType.DIGITAL_CNH && !isUserMinor && (
            <div>
              <p className="select-rg-label">
                {translate(
                  'PAGES.AUTH.REGISTER.ONBOARD.PF.DOCUMENT.FORM.LABELS.CNH.DIGITAL'
                )}
              </p>

              <div className="onboarding-user-photo">
                {!onFileChangeDigitalCnhModal?.name ? (
                  <div className="onboarding-user-photo__container">
                    <label htmlFor="file-cnh">
                      {translate(
                        'PAGES.AUTH.REGISTER.ONBOARD.PF.DOCUMENT.FORM.LOAD_FILE'
                      )}
                    </label>
                    <div className="onboarding-user-photo__title">
                      {onboarding.maxFileSizeImage.enabled ? (
                        <p>
                          {translate(
                            'PAGES.AUTH.REGISTER.ONBOARD.PF.DOCUMENT.FORM.UPLOAD_MAX_IMAGE'
                          )}
                          {onboarding.maxFileSizeImage.sizeInMb}
                          {translate('SHARED.MB')}
                        </p>
                      ) : null}
                      {onboarding.maxFileSizePdf.enabled ? (
                        <p>
                          {translate(
                            'PAGES.AUTH.REGISTER.ONBOARD.PF.DOCUMENT.FORM.UPLOAD_MAX_PDF'
                          )}
                          {onboarding.maxFileSizePdf.sizeInMb}
                          {translate('SHARED.MB')}
                        </p>
                      ) : null}
                    </div>
                    <input
                      className="onboarding-user-photo__input"
                      type="file"
                      name="file-cnh"
                      id="file-cnh"
                      onChange={onFileChangeDigitalCnh}
                      accept="application/pdf"
                    />
                  </div>
                ) : (
                  <div>
                    <div className="message-modal__fileSelected-button">
                      <span>
                        <iframe
                          src={`${URL.createObjectURL(
                            onFileChangeDigitalCnhModal
                          )}#toolbar=0`}
                          width="90%"
                          height={400}
                          title="upload document"
                        />
                      </span>
                    </div>
                    <div className="upload-again__container">
                      <label htmlFor="file-again-cnh">
                        <p className="message-modal__fileSelected-text">
                          {translate(
                            'PAGES.AUTH.REGISTER.ONBOARD.PF.DOCUMENT.FORM.UPLOAD_AGAIN'
                          )}
                        </p>
                      </label>
                      <input
                        className="onboarding-user-photo__input"
                        type="file"
                        name="file-again-cnh"
                        id="file-again-cnh"
                        onChange={onFileChangeDigitalCnh}
                        accept="application/pdf"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {documentType === DocumentType.RG && (
            <div>
              <div className="onboarding-document-photo__text">
                <p className="select-rg-label">
                  {translate(
                    'PAGES.AUTH.REGISTER.ONBOARD.PF.DOCUMENT.FORM.LABELS.RG.FRONT'
                  )}
                </p>
              </div>
              <div className="onboarding-user-photo">
                {!onFileChangeRgFrontModal?.name ? (
                  <div className="onboarding-user-photo__container">
                    <label
                      htmlFor="file-front"
                      className="onboarding-user-photo__button"
                    >
                      {translate(
                        'PAGES.AUTH.REGISTER.ONBOARD.PF.DOCUMENT.FORM.LOAD_FILE'
                      )}
                    </label>
                    <div className="onboarding-user-photo__title">
                      {onboarding.maxFileSizeImage.enabled ? (
                        <p>
                          {translate(
                            'PAGES.AUTH.REGISTER.ONBOARD.PF.DOCUMENT.FORM.UPLOAD_MAX_IMAGE'
                          )}
                          {onboarding.maxFileSizeImage.sizeInMb}
                          {translate('SHARED.MB')}
                        </p>
                      ) : null}
                      {onboarding.maxFileSizePdf.enabled ? (
                        <p>
                          {translate(
                            'PAGES.AUTH.REGISTER.ONBOARD.PF.DOCUMENT.FORM.UPLOAD_MAX_PDF'
                          )}
                          {onboarding.maxFileSizePdf.sizeInMb}
                          {translate('SHARED.MB')}
                        </p>
                      ) : null}
                    </div>
                    <input
                      className="onboarding-user-photo__input"
                      type="file"
                      name="file-front"
                      id="file-front"
                      onChange={onFileChangeRgFront}
                      accept={`image/png, image/jpeg, ${
                        onboarding.acceptPdfDocs ? 'application/pdf' : ''
                      }`}
                    />
                  </div>
                ) : (
                  <div>
                    <div className="message-modal__fileSelected-button">
                      <span>
                        <div onClick={cleanFileRgFront}>
                          <CloseOutlined />
                        </div>
                        {onboarding.acceptPdfDocs &&
                        onFileChangeRgFrontModal.type === 'application/pdf' ? (
                          <iframe
                            src={(() =>
                              URL.createObjectURL(onFileChangeRgFrontModal))()}
                            className="pending-document-modal__preview__container__image"
                            title="upload document"
                          />
                        ) : (
                          <img
                            src={(() =>
                              URL.createObjectURL(onFileChangeRgFrontModal))()}
                            alt="upload image"
                            className="pending-document-modal__preview__container__image"
                          />
                        )}
                      </span>
                    </div>
                  </div>
                )}
              </div>
              <div className="onboarding-document-photo__text">
                <p className="select-rg-label">
                  {translate(
                    'PAGES.AUTH.REGISTER.ONBOARD.PF.DOCUMENT.FORM.LABELS.RG.BACK'
                  )}
                </p>
              </div>
              <div className="onboarding-user-photo">
                {!onFileChangeRgBackModal?.name ? (
                  <div className={'onboarding-user-photo__container'}>
                    <div>
                      <label
                        htmlFor="file-back"
                        className="onboarding-user-photo__button"
                      >
                        {translate(
                          'PAGES.AUTH.REGISTER.ONBOARD.PF.DOCUMENT.FORM.LOAD_FILE'
                        )}
                      </label>
                      <div className="onboarding-user-photo__title">
                        {onboarding.maxFileSizeImage.enabled ? (
                          <p>
                            {translate(
                              'PAGES.AUTH.REGISTER.ONBOARD.PF.DOCUMENT.FORM.UPLOAD_MAX_IMAGE'
                            )}
                            {onboarding.maxFileSizeImage.sizeInMb}
                            {translate('SHARED.MB')}
                          </p>
                        ) : null}
                        {onboarding.maxFileSizePdf.enabled ? (
                          <p>
                            {translate(
                              'PAGES.AUTH.REGISTER.ONBOARD.PF.DOCUMENT.FORM.UPLOAD_MAX_PDF'
                            )}
                            {onboarding.maxFileSizePdf.sizeInMb}
                            {translate('SHARED.MB')}
                          </p>
                        ) : null}
                      </div>
                      <input
                        className="onboarding-user-photo__input"
                        type="file"
                        name="file-back"
                        id="file-back"
                        onChange={onFileChangeRgBack}
                        accept={`image/png, image/jpeg, ${
                          onboarding.acceptPdfDocs ? 'application/pdf' : ''
                        }`}
                      />
                    </div>
                  </div>
                ) : (
                  <div>
                    <div className="message-modal__fileSelected-button">
                      <span>
                        <div onClick={cleanFileRgBack}>
                          <CloseOutlined />
                        </div>
                        {onboarding.acceptPdfDocs &&
                        onFileChangeRgBackModal.type === 'application/pdf' ? (
                          <iframe
                            src={(() =>
                              URL.createObjectURL(onFileChangeRgBackModal))()}
                            className="pending-document-modal__preview__container__image"
                            title="upload document"
                          />
                        ) : (
                          <img
                            src={(() =>
                              URL.createObjectURL(onFileChangeRgBackModal))()}
                            alt="upload image"
                            className="pending-document-modal__preview__container__image"
                          />
                        )}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </Col>
      </Col>

      <div className="message-modal__button-continue">
        <AdvancedButton
          disabled={
            (documentType === DocumentType.CNH && !onFileChangeCnhModal) ||
            (documentType === DocumentType.RG &&
              (!onFileChangeRgFrontModal || !onFileChangeRgBackModal))
          }
          text="Confirmar"
          onClick={handleCloseModal}
          customStyles={{
            width: '141px',
            height: '40px',
            borderRadius: '4px',
          }}
        />
      </div>
    </Modal>
  );
};

export default OnboardingDocumentPhotoModal;
